import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, EditableText, NonIdealState } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { TorgaContext } from '../TorgaContext';
import { Page } from '../../utils/Layout/Layout';

import {
  actionTaskDelete,
  actionTaskUpdate,
} from './actions';
import { Link } from 'react-router-dom';
import { urlToActivityID, urlToGoalID } from '../TorgaRouter';

const panelStyle = {
  border: '1px solid #2f3a45',
  borderRadius: 8,
  padding: 12,
  marginBottom: 12,
};

const panelTitleStyle = { marginTop: 0, marginBottom: 10 };

const fieldBlockStyle = { marginBottom: 10 };

const labelStyle = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 6,
};

const inputStyle = { width: '100%', padding: 8 };

const readOnlyGridStyle = {
  display: 'grid',
  gridTemplateColumns: '120px 1fr',
  gap: 6,
  alignItems: 'center',
};

export default function Task() {
  const { state, dispatch, dispatchAction } = React.useContext(TorgaContext);
  const [goalSearch, setGoalSearch] = React.useState('');
  const [activitySearch, setActivitySearch] = React.useState('');
  const [selectedDirectGoalID, setSelectedDirectGoalID] = React.useState('');
  const [selectedDirectActivityID, setSelectedDirectActivityID] = React.useState('');
  const [isNameEditing, setIsNameEditing] = React.useState(false);
  const [draft, setDraft] = React.useState({
    description: '',
    status: '',
    notes: '',
  });
  const location = useLocation();
  const navigate = useNavigate();
  let { id: prefixedID } = useParams();
  const id = prefixedID?.slice(1);

  const { [id]: task } = state.tasks.byID;
  const allGoals = state.goals.allIDs
    .map((goalID) => state.goals.byID[goalID])
    .filter((goal) => goal && !goal.deleted);
  const allActivities = state.activities.allIDs
    .map((activityID) => state.activities.byID[activityID])
    .filter((activity) => activity && !activity.deleted);
  const filteredGoalOptions = allGoals.filter((goal) => {
    if (!goalSearch.trim()) {
      return true;
    }
    return String(goal.name || '')
      .toLowerCase()
      .includes(goalSearch.toLowerCase());
  });
  const filteredActivityOptions = allActivities.filter((activity) => {
    if (!activitySearch.trim()) {
      return true;
    }
    return String(activity.name || '')
      .toLowerCase()
      .includes(activitySearch.toLowerCase());
  });
  const currentGoal = task?.goal_id ? state.goals.byID[task.goal_id] : null;
  const currentActivity = task?.activity_id
    ? state.activities.byID[task.activity_id]
    : null;

  React.useEffect(() => {
    if (!task) {
      return;
    }
    if (!location.state?.focusName) {
      return;
    }
    setIsNameEditing(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate, task]);

  React.useEffect(() => {
    if (!task) {
      return;
    }

    setDraft({
      description: task.description || '',
      status: task.status || '',
      notes: task.notes || '',
    });
  }, [task]);

  if (!task) {
    return <NonIdealState icon={IconNames.TICK} />;
  }

  function handleChange(field, value) {
    dispatch({ type: 'TASK_CHANGE', payload: { id, field, value } });
  }

  function handleConfirm(field, value) {
    dispatchAction(actionTaskUpdate(id, field, value));
  }

  function normalizeNullableString(value) {
    const trimmed = String(value || '').trim();
    return trimmed ? trimmed : null;
  }

  return (
    <Page>
      <h1>
        <EditableText
          data-testid="task-name-editable"
          placeholder="Task Name"
          value={task.name}
          onChange={(e) => handleChange('name', e)}
          onConfirm={(e) => handleConfirm('name', e)}
          onEdit={(editing) => setIsNameEditing(editing)}
          isEditing={isNameEditing}
          selectAllOnFocus={true}
        />
      </h1>

      <div style={panelStyle}>
        <h3 style={panelTitleStyle}>Editable fields</h3>

        <div style={fieldBlockStyle}>
          <label style={labelStyle}>Goal (optional)</label>
          <input
            type="text"
            style={{ ...inputStyle, marginBottom: 8 }}
            placeholder="Search goal..."
            value={goalSearch}
            onChange={(event) => setGoalSearch(event.target.value)}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <select
              style={{ ...inputStyle, flex: 1 }}
              value={selectedDirectGoalID}
              onChange={(event) => setSelectedDirectGoalID(event.target.value)}
            >
              <option value="">Select goal...</option>
              {filteredGoalOptions.map((goal) => (
                <option key={goal.id} value={goal.id}>
                  G{goal.id} - {goal.name}
                </option>
              ))}
            </select>
            <Button
              small
              disabled={!selectedDirectGoalID}
              onClick={() => handleConfirm('goal_id', Number(selectedDirectGoalID))}
            >
              Associate
            </Button>
            <Button
              small
              disabled={!task.goal_id}
              onClick={() => handleConfirm('goal_id', null)}
            >
              Dissociate
            </Button>
          </div>
          {currentGoal && (
            <div style={{ marginTop: 6 }}>
              Current: <Link to={urlToGoalID(currentGoal.id)}>G{currentGoal.id} - {currentGoal.name}</Link>
            </div>
          )}
        </div>

        <div style={fieldBlockStyle}>
          <label style={labelStyle}>Activity (optional)</label>
          <input
            type="text"
            style={{ ...inputStyle, marginBottom: 8 }}
            placeholder="Search activity..."
            value={activitySearch}
            onChange={(event) => setActivitySearch(event.target.value)}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <select
              style={{ ...inputStyle, flex: 1 }}
              value={selectedDirectActivityID}
              onChange={(event) => setSelectedDirectActivityID(event.target.value)}
            >
              <option value="">Select activity...</option>
              {filteredActivityOptions.map((activity) => (
                <option key={activity.id} value={activity.id}>
                  A{activity.id} - {activity.name}
                </option>
              ))}
            </select>
            <Button
              small
              disabled={!selectedDirectActivityID}
              onClick={() =>
                handleConfirm('activity_id', Number(selectedDirectActivityID))
              }
            >
              Associate
            </Button>
            <Button
              small
              disabled={!task.activity_id}
              onClick={() => handleConfirm('activity_id', null)}
            >
              Dissociate
            </Button>
          </div>
          {currentActivity && (
            <div style={{ marginTop: 6 }}>
              Current:{' '}
              <Link to={urlToActivityID(currentActivity.id)}>
                A{currentActivity.id} - {currentActivity.name}
              </Link>
            </div>
          )}
        </div>

        <div style={fieldBlockStyle}>
          <label style={labelStyle}>Description</label>
          <textarea
            style={inputStyle}
            value={draft.description}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                description: event.target.value,
              }))
            }
            rows={4}
          />
          <div style={{ marginTop: 6 }}>
            <Button
              small
              onClick={() =>
                handleConfirm(
                  'description',
                  normalizeNullableString(draft.description)
                )
              }
            >
              Save description
            </Button>
          </div>
        </div>

        <div style={fieldBlockStyle}>
          <label style={labelStyle}>Status</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              style={inputStyle}
              value={draft.status}
              onChange={(event) =>
                setDraft((current) => ({ ...current, status: event.target.value }))
              }
            />
            <Button
              small
              onClick={() =>
                handleConfirm('status', normalizeNullableString(draft.status))
              }
            >
              Save
            </Button>
          </div>
        </div>

        <div style={fieldBlockStyle}>
          <label style={labelStyle}>Notes</label>
          <textarea
            style={inputStyle}
            value={draft.notes}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                notes: event.target.value,
              }))
            }
            rows={4}
          />
          <div style={{ marginTop: 6 }}>
            <Button
              small
              onClick={() =>
                handleConfirm('notes', normalizeNullableString(draft.notes))
              }
            >
              Save notes
            </Button>
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={task.deleted === true}
            onChange={(event) => handleConfirm('deleted', event.target.checked)}
          />
          deleted
        </label>
      </div>

      <div style={panelStyle}>
        <h3 style={panelTitleStyle}>DB fields (read-only)</h3>
        <div style={readOnlyGridStyle}>
          <strong>id</strong>
          <code>{task.id}</code>
          <strong>user_id</strong>
          <code>{task.user_id}</code>
          <strong>createdAt</strong>
          <code>{task.createdAt ? String(task.createdAt) : '-'}</code>
          <strong>updatedAt</strong>
          <code>{task.updatedAt ? String(task.updatedAt) : '-'}</code>
          <strong>activity_id</strong>
          <code>{task.activity_id ?? '-'}</code>
          <strong>goal_id</strong>
          <code>{task.goal_id ?? '-'}</code>
        </div>
      </div>

      <Button
        data-testid="task-delete-button"
        icon={IconNames.TRASH}
        minimal
        onClick={() => dispatchAction(actionTaskDelete(id))}
      >
        Delete task
      </Button>
    </Page>
  );
}
